import React, { useEffect, useState } from 'react';
import { TrendingUp } from 'lucide-react';
import { motion, useSpring, useTransform } from 'framer-motion';

const AnimatedCounter = ({ value }: { value: number }) => {
  const spring = useSpring(0, { mass: 0.8, stiffness: 75, damping: 15 });
  const display = useTransform(spring, (current) => Math.round(current));
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    if (hasStarted) {
      spring.set(value);
    }
  }, [spring, value, hasStarted]);

  return (
    <motion.span onViewportEnter={() => setHasStarted(true)}>
      {display}
    </motion.span>
  );
};

const ValueProp: React.FC = () => {
  return (
    <section className="py-16 md:py-24 lg:py-32 2xl:px-52 px-0 bg-dark overflow-hidden">
      <div className="lg:px-24 px-6 md:px-8 flex flex-col md:flex-row items-center gap-12 md:gap-16 lg:gap-24">
        
        {/* Left Column: Text */}
        <motion.div 
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="w-full md:w-3/5"
        >
          <div className="inline-block px-4 py-1 rounded-full bg-pink/10 text-pink font-bold text-sm mb-6 border border-pink/20">
            PROVEN TRACK RECORD
          </div>
          <h2 className="font-semibold text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-10 leading-[1.3] text-white">
            100M+ views generated for games like yours.
          </h2>
          <div className="relative pl-8">
            {/* Animated Squiggle Line */}
            <motion.svg 
              className="absolute top-0 left-0 h-full w-4 text-pink" 
              viewBox="0 0 10 100" 
              preserveAspectRatio="none"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.3 }}
            >
              <motion.path 
                d="M 5,0 Q 0,10 5,20 Q 10,30 5,40 Q 0,50 5,60 Q 10,70 5,80 Q 0,90 5,100"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 1.2, ease: "easeOut", delay: 0.2 }}
              />
            </motion.svg>
            <p className="text-lg md:text-xl text-muted leading-relaxed">
              Our systematic approach to viral content has scaled some of the biggest games in Minecraft and Roblox. We don't guess—we engineer growth.
            </p>
          </div>
          <div className="mt-10 flex gap-12">
            <div>
              <p className="text-3xl md:text-4xl font-bold text-white">100M+</p>
              <p className="text-muted font-medium">Total Views</p>
            </div>
            <div>
              <p className="text-3xl md:text-4xl font-bold text-white flex items-center">
                <AnimatedCounter value={1073} />
              </p>
              <p className="text-muted font-medium">Videos Created</p>
            </div>
          </div>
        </motion.div>

        {/* Right Column: Visual */}
        <motion.div 
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="w-full md:w-2/5 relative"
        >
          <div className="relative aspect-square">
            {/* Abstract visual background */}
            <div className="absolute inset-0 bg-gradient-to-br from-pink to-accent rounded-[3rem] transform rotate-3 scale-95 opacity-20 blur-xl" />
            
            <div className="absolute inset-0 bg-dark-alt rounded-[2rem] border border-white/10 flex items-center justify-center overflow-hidden shadow-2xl">
              {/* Mock Graph */}
              <div className="w-full h-full p-6 md:p-8 flex flex-col justify-end relative">
                <div className="absolute top-6 md:top-8 left-6 md:left-8">
                  <div className="text-muted text-xs md:text-sm font-bold">AVG. VIEW COUNT</div>
                  <div className="text-white text-2xl md:text-3xl font-bold flex items-center gap-2">
                    2.4M <span className="text-green-400 text-xs md:text-sm bg-green-400/10 px-2 py-1 rounded-full">+450%</span>
                  </div>
                </div>
                
                {/* CSS Bars Graph */}
                <div className="flex items-end justify-between gap-2 flex-1 mt-20">
                  {[45, 55, 50, 70, 60, 75, 95, 90].map((h, i) => (
                    <motion.div 
                      key={i}
                      initial={{ height: 0 }}
                      whileInView={{ height: `${h}%` }}
                      viewport={{ once: true, amount: 0.5 }}
                      transition={{ duration: 0.5, delay: 0.2 + i * 0.1 }}
                      className={`w-full rounded-t-lg ${i > 5 ? 'bg-pink' : 'bg-accent/30'}`} 
                    />
                  ))}
                </div>
              </div>
            </div>
            
            {/* Floating Stats */}
            <motion.div 
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-4 -right-4 bg-white text-dark px-4 md:px-6 py-3 md:py-4 rounded-xl font-bold transform rotate-6 shadow-xl border-b-4 border-pink"
            >
              <div className="flex items-center gap-2 text-sm md:text-base">
                <TrendingUp size={20} className="text-pink" />
                <span>Top 1% Growth</span>
              </div>
            </motion.div>
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default ValueProp;
